import { useQuery, useMutation, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import AgencyTokenManager from "@/app/_module/services/agencyTokenManager";
import { communicationService, CallsQueryParams } from "../services/communicationService";

// Memoized agency context to prevent infinite re-renders
let cachedAgencyContext: { agencyId: string | null } | null = null;
let lastAgencyId: string | null = null;

// Helper function to get current agency context for query keys
const getAgencyContext = () => {
  const agencyId = AgencyTokenManager.getCurrentAgencyId();

  // Only create new context if agency ID has changed
  if (agencyId !== lastAgencyId) {
    lastAgencyId = agencyId;
    cachedAgencyContext = agencyId ? { agencyId } : { agencyId: null };
  }

  return cachedAgencyContext!;
};

// Query keys for communication data
export const communicationKeys = {
  all: () => ["communication", getAgencyContext()] as const,
  calls: () => ["communication", "calls", getAgencyContext()] as const,
  call: (params: CallsQueryParams = {}) => ["communication", "calls", params, getAgencyContext()] as const,
  callById: (id: string) => ["communication", "calls", id, getAgencyContext()] as const,
  sms: () => ["communication", "sms", getAgencyContext()] as const,
  smsWithParams: (params: any = {}) => ["communication", "sms", params, getAgencyContext()] as const,
  emails: () => ["communication", "emails", getAgencyContext()] as const,
  emailsWithParams: (params: any = {}) => ["communication", "emails", params, getAgencyContext()] as const,
  voicemails: () => ["communication", "voicemails", getAgencyContext()] as const,
};

/**
 * Fetch call history with optional filters
 * Automatically refetches on mount and agency changes
 */
export const useCalls = (params?: CallsQueryParams, options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) => {
  return useQuery({
    queryKey: communicationKeys.call(params || {}),
    queryFn: async () => {
      const data = await communicationService.getCalls(params);
      return data;
    },
    staleTime: 30 * 1000, // 30 seconds - reduced for real-time updates
    refetchOnMount: true, // Always refetch on mount
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    ...options,
  });
};

/**
 * Fetch a single call by ID
 */
export const useCallById = (callId: string, options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) => {
  return useQuery({
    queryKey: communicationKeys.callById(callId),
    queryFn: async () => {
      const data = await communicationService.getCallById(callId);
      return data;
    },
    enabled: !!callId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: true,
    ...options,
  });
};

/**
 * Fetch SMS history with optional filters
 * Automatically refetches on mount and agency changes
 * Supports real-time polling via refetchInterval option
 */
export const useSMS = (params?: any, options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) => {
  return useQuery({
    queryKey: communicationKeys.smsWithParams(params || {}),
    queryFn: async () => {
      const data = await communicationService.getSms(params);
      return data;
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Poll every 60s — realtime handles instant delivery
    refetchIntervalInBackground: false, // Only poll when window is focused
    refetchOnMount: true, // Always refetch on mount
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    ...options,
  });
};

/**
 * Fetch Email history with optional filters
 * Automatically refetches on mount and agency changes
 * Supports real-time polling via refetchInterval option
 * Backend syncs emails every 1 minute, so we poll every 15 seconds to catch updates
 */
export const useEmails = (params?: {
  leadId?: string;
  status?: string;
  type?: string;
  search?: string;
  page?: number;
  limit?: number;
}, options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) => {
  return useQuery({
    queryKey: communicationKeys.emailsWithParams(params || {}),
    queryFn: async () => {
      const data = await communicationService.getEmails(params);
      return data;
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Poll every 60 seconds (backend syncs every ~1 minute)
    refetchIntervalInBackground: false, // Only poll when window is focused
    refetchOnMount: true, // Always refetch on mount
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    ...options,
  });
};

/**
 * Fetch voicemails (calls with outcome='voicemail')
 * Automatically refetches on mount and agency changes
 */
export const useVoicemails = (options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) => {
  return useQuery({
    queryKey: communicationKeys.voicemails(),
    queryFn: async () => {
      const data = await communicationService.getVoicemails();
      return data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnMount: true, // Always refetch on mount
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    ...options,
  });
};

/**
 * Mutation hook for creating/sending SMS
 * Automatically invalidates SMS queries on success
 */
export const useSendSMS = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      // You'll need to implement the send SMS service method
      // For now, returning placeholder
      throw new Error("Send SMS mutation not yet implemented in communicationService");
    },
    onSuccess: () => {
      // Invalidate and refetch SMS queries
      queryClient.invalidateQueries({ queryKey: communicationKeys.sms() });
    },
  });
};

/**
 * Mutation hook for making calls
 * Automatically invalidates call queries on success
 */
export const useMakeCall = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      // You'll need to implement the make call service method
      // For now, returning placeholder
      throw new Error("Make call mutation not yet implemented in communicationService");
    },
    onSuccess: () => {
      // Invalidate and refetch call queries
      queryClient.invalidateQueries({ queryKey: communicationKeys.calls() });
    },
  });
};

/**
 * Mutation hook for updating call notes/outcome
 * Automatically invalidates call queries on success
 */
export const useUpdateCall = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ callId, data }: { callId: string; data: any }) => {
      // You'll need to implement the update call service method
      // For now, returning placeholder
      throw new Error("Update call mutation not yet implemented in communicationService");
    },
    onSuccess: (_, variables) => {
      // Invalidate specific call and all calls list
      queryClient.invalidateQueries({ queryKey: communicationKeys.callById(variables.callId) });
      queryClient.invalidateQueries({ queryKey: communicationKeys.calls() });
    },
  });
};

/**
 * Mutation hook for marking voicemail as listened
 * Automatically invalidates voicemail queries on success
 */
export const useMarkVoicemailAsListened = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (voicemailId: string) => {
      // Call API to mark voicemail as listened
      const result = await communicationService.markVoicemailAsListened(voicemailId);
      return result;
    },
    onSuccess: () => {
      // Invalidate voicemails query to refetch updated data
      queryClient.invalidateQueries({ queryKey: communicationKeys.voicemails() });
      queryClient.invalidateQueries({ queryKey: communicationKeys.calls() });
    },
  });
};

/**
 * Mutation hook for deleting voicemail
 * Automatically invalidates voicemail queries on success
 */
export const useDeleteVoicemail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (voicemailId: string) => {
      // Call API to delete voicemail
      const result = await communicationService.deleteVoicemail(voicemailId);
      return result;
    },
    onSuccess: () => {
      // Invalidate voicemails query to refetch updated data
      queryClient.invalidateQueries({ queryKey: communicationKeys.voicemails() });
      queryClient.invalidateQueries({ queryKey: communicationKeys.calls() });
    },
  });
};

/**
 * Mutation hook for syncing emails for a specific lead from OAuth provider
 * Triggered on-demand when user opens lead details or email history tab
 * Automatically invalidates email queries on success to show new emails
 */
export const useSyncLeadEmails = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (leadId: string) => {
      const result = await communicationService.syncLeadEmails(leadId);
      return result;
    },
    onSuccess: (result, leadId) => {
      // Invalidate email queries to show newly synced emails
      queryClient.invalidateQueries({ queryKey: communicationKeys.emails() });
      queryClient.invalidateQueries({ queryKey: communicationKeys.emailsWithParams({ leadId }) });
    },
  });
};
